import * as THREE from 'three';
import * as TSL from 'three/tsl';
import { MeshStandardNodeMaterial } from 'three/webgpu';

import { UrlTexture } from '../../../render/Texture.js';
import { EngineClass } from '../../../systems/ClassRegistry.js';
// Registers UrlTexture loader before constructor UrlTextures run (serialization CDO).
import '../../../utils/ResourceManager.js';
import { property } from '../../../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../../../utils/serialization/serializable.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** Parallax UV overlay matching three.js `webgpu_parallax_uv.html`. */
@EngineClass('ParallaxUVNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Parallax UV',
  nodeMaterialGroup: 'TSL Node FX',
})
export class ParallaxUVNodeMaterialAsset extends NodeMaterialAsset(MeshStandardNodeMaterial) {
  readonly [ISerializableObjectTag] = true as const;

  @property({ type: 'number', min: 0.01, max: 100.0, step: 0.01, description: 'UV tiling scale' })
    uvScale = 3.0;

  @property({ type: 'number', min: 0.0, max: 2.0, step: 0.01, description: 'Parallax strength from height map' })
    parallaxScale = 0.5;

  @property({ type: 'number', min: 0.0, max: 20.0, step: 0.01, description: 'Multiplier on blended base color' })
    colorGain = 5.0;

  @property({ type: 'number', min: 0.0, max: 1.0, step: 0.01, description: 'Metalness' })
  override metalness = 0.0;

  @property({ type: 'texturePath', description: 'Top layer albedo' })
    topColorUrl: TexturePath = '@engine/assets/textures/T_ParallaxUV_BaseColor.jpg';

  @property({ type: 'texturePath', description: 'Roughness map' })
    roughnessUrl: TexturePath = '@engine/assets/textures/T_ParallaxUV_Roughness.jpg';

  @property({ type: 'texturePath', description: 'Normal map GL' })
    normalGlUrl: TexturePath = '@engine/assets/textures/T_ParallaxUV_Normal.jpg';

  @property({ type: 'texturePath', description: 'Height / displacement map for parallax offset' })
    displacementUrl: TexturePath = '@engine/assets/textures/T_ParallaxUV_Displacement.jpg';

  @property({ type: 'texturePath', description: 'Bottom layer albedo sampled with parallax UV' })
    bottomColorUrl: TexturePath = '@engine/assets/textures/T_ParallaxUV_Bottom_BaseColor.jpg';

  private readonly _uvScaleUniform = TSL.uniform(this.uvScale);
  private readonly _parallaxScaleUniform = TSL.uniform(this.parallaxScale);
  private readonly _colorGainUniform = TSL.uniform(this.colorGain);
  private readonly _metalnessUniform = TSL.uniform(this.metalness);

  private readonly _topColorTexture: UrlTexture;
  private readonly _roughnessTexture: UrlTexture;
  private readonly _normalTexture: UrlTexture;
  private readonly _displacementTexture: UrlTexture;
  private readonly _bottomColorTexture: UrlTexture;

  constructor() {
    super();

    this._topColorTexture = new UrlTexture({
      url: this.topColorUrl,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      colorSpace: THREE.SRGBColorSpace,
    });
    this._roughnessTexture = new UrlTexture({
      url: this.roughnessUrl,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      colorSpace: THREE.NoColorSpace,
    });
    this._normalTexture = new UrlTexture({
      url: this.normalGlUrl,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      colorSpace: THREE.NoColorSpace,
    });
    this._displacementTexture = new UrlTexture({
      url: this.displacementUrl,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      colorSpace: THREE.NoColorSpace,
    });
    this._bottomColorTexture = new UrlTexture({
      url: this.bottomColorUrl,
      wrapS: THREE.RepeatWrapping,
      wrapT: THREE.RepeatWrapping,
      colorSpace: THREE.SRGBColorSpace,
    });

    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    const uvScaleClamped = this.clampFiniteNumber(this.uvScale, 0.01, 100, 3.0);
    const parallaxClamped = this.clampFiniteNumber(this.parallaxScale, 0, 2, 0.5);
    const gainClamped = this.clampFiniteNumber(this.colorGain, 0, 20, 5.0);
    const metalClamped = this.clampFiniteNumber(this.metalness, 0, 1, 0.0);
    this.uvScale = uvScaleClamped;
    this.parallaxScale = parallaxClamped;
    this.colorGain = gainClamped;
    this.metalness = metalClamped;

    this._uvScaleUniform.value = uvScaleClamped;
    this._parallaxScaleUniform.value = parallaxClamped;
    this._colorGainUniform.value = gainClamped;
    this._metalnessUniform.value = metalClamped;

    this.topColorUrl = this.coerceTexturePath(this.topColorUrl) || '@engine/assets/textures/T_ParallaxUV_BaseColor.jpg';
    this.roughnessUrl = this.coerceTexturePath(this.roughnessUrl) || '@engine/assets/textures/T_ParallaxUV_Roughness.jpg';
    this.normalGlUrl = this.coerceTexturePath(this.normalGlUrl) || '@engine/assets/textures/T_ParallaxUV_Normal.jpg';
    this.displacementUrl = this.coerceTexturePath(this.displacementUrl) || '@engine/assets/textures/T_ParallaxUV_Displacement.jpg';
    this.bottomColorUrl = this.coerceTexturePath(this.bottomColorUrl) || '@engine/assets/textures/T_ParallaxUV_Bottom_BaseColor.jpg';

    this.syncTextureUrl(this._topColorTexture, this.topColorUrl, THREE.SRGBColorSpace);
    this.syncTextureUrl(this._roughnessTexture, this.roughnessUrl, THREE.NoColorSpace);
    this.syncTextureUrl(this._normalTexture, this.normalGlUrl, THREE.NoColorSpace);
    this.syncTextureUrl(this._displacementTexture, this.displacementUrl, THREE.NoColorSpace);
    this.syncTextureUrl(this._bottomColorTexture, this.bottomColorUrl, THREE.SRGBColorSpace);

    try {
      const scaledUV = TSL.uv().mul(this._uvScaleUniform);
      // parallaxUV expects a vec2 scale: R/G channels become independent X/Y offsets.
      // Collapsing to a single channel here would broadcast a uniform scalar offset along
      // parallaxDirection and produce 1D streak artifacts instead of natural parallax.
      const offsetUV = TSL.texture(this._displacementTexture, scaledUV).mul(this._parallaxScaleUniform);
      const parallaxUVOffset = TSL.parallaxUV(scaledUV, offsetUV);
      const parallaxResult = TSL.texture(this._bottomColorTexture, parallaxUVOffset);
      const blendedParallaxNode = TSL.blendOverlay(TSL.texture(this._topColorTexture, scaledUV), parallaxResult);

      this.colorNode = blendedParallaxNode.mul(this._colorGainUniform);
      this.roughnessNode = TSL.texture(this._roughnessTexture, scaledUV);
      this.normalNode = TSL.normalMap(TSL.texture(this._normalTexture, scaledUV));
      this.metalnessNode = this._metalnessUniform;
    } catch {
      this.colorNode = null;
      this.normalNode = null;
      this.roughnessNode = TSL.float(0.5);
      this.metalnessNode = TSL.float(metalClamped);
    }
  }

  private syncTextureUrl(tex: UrlTexture, url: string, colorSpace: THREE.ColorSpace): void {
    try {
      if (tex.url !== url) {
        tex.url = url;
      }
      tex.colorSpace = colorSpace;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.needsUpdate = true;
    } catch {
      // Keep previous texture state on invalid URL updates.
    }
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): ParallaxUVNodeMaterialAsset {
    const instance = new ParallaxUVNodeMaterialAsset();
    ParallaxUVNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
